import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Bot, RefreshCw, AlertCircle, CheckCircle2, CloudLightning, ExternalLink, Zap } from 'lucide-react';

const SophieManagementSection: React.FC = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [genResult, setGenResult] = useState<string>('');
    const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'stopping'>('idle');
    const [batchSize, setBatchSize] = useState([30]);
    
    // NEW: Iteration Limit Control
    const [iterationLimit, setIterationLimit] = useState({
        enabled: false,
        count: 1
    });
    
    // Control Ref to abort loops
    const stopRef = React.useRef(false);
    
    const [stats, setStats] = useState({
        totalSOPs: 0,
        lastSyncDate: '-',
        lastEmbeddedCount: 0,
        currentJobProcessed: 0, // NEW
        totalCount: 0, 
        lastEmbeddedDate: '-',
        status: 'idle',
        progress: 0
    });

    // Realtime Stats Listener
    React.useEffect(() => {
        let unsubscribe = () => {};

        const setupListener = async () => {
             // 1. Get Initial SOP Metrics & Sync Status (One-time fetch)
             try {
                const { sopService } = await import('../../services/firebase/SOPService');
                const [metrics, syncStatus] = await Promise.all([
                    sopService.getSOPMetrics(),
                    sopService.getSyncStatus()
                ]);
                
                setStats(prev => ({
                    ...prev,
                    totalSOPs: metrics.totalSOPs,
                    lastSyncDate: syncStatus.lastSync ? syncStatus.lastSync.toLocaleDateString() + ' ' + syncStatus.lastSync.toLocaleTimeString() : '-',
                }));
             } catch (e) {
                 console.error("Error fetching static stats:", e);
             }

             // 2. Setup Realtime Listener for Embedding Stats
             try {
                const { db } = await import('../../config/firebase');
                const { doc, onSnapshot } = await import('firebase/firestore');
                
                unsubscribe = onSnapshot(doc(db, 'sophieStats', 'embeddings'), (doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        const processed = data.processedCount || 0;
                        const totalEmbedded = data.totalEmbedded || 0;

                        // GLOBAL PROGRESS: Total Embedded / Total SOPs
                        // We use the callback's previous state to get totalSOPs correctly
                        // The actual calculation happens in setStats below
                        
                        // Staleness check
                        const lastRunTime = data.lastRun ? data.lastRun.seconds * 1000 : 0;
                        const timeSinceLastRun = Date.now() - lastRunTime;
                        // Increased staleness threshold to 4 mins to account for large batches
                        const isStale = timeSinceLastRun > 4 * 60 * 1000; 

                        let currentStatus = data.status || 'idle';
                        if (isStale && currentStatus === 'processing') {
                            currentStatus = 'stale';
                        }

                        setStats(prev => {
                            // Use backend's totalEmbeddable if available (accurate count excluding "OLD DO NOT USE")
                            const embeddableTotal = data.totalEmbeddable || prev.totalSOPs;
                            return {
                                ...prev,
                                lastEmbeddedDate: data.lastRun ? new Date(data.lastRun.seconds * 1000).toLocaleString() : '-',
                                lastEmbeddedCount: totalEmbedded || processed,
                                currentJobProcessed: processed, 
                                totalCount: data.totalCount || embeddableTotal,
                                totalSOPs: embeddableTotal, // Update totalSOPs with accurate backend count
                                status: currentStatus,
                                progress: embeddableTotal > 0 ? Math.round((totalEmbedded / embeddableTotal) * 100) : 0
                            };
                        });

                        // Sync UI state, BUT respect manual stopping
                        if (!stopRef.current) {
                             if (currentStatus === 'processing') {
                                setIsGenerating(true);
                            } else if (currentStatus === 'completed') {
                                setIsGenerating(false);
                                setStatus('success');
                                setGenResult('All items processed successfully.');
                            } else if (currentStatus === 'idle') {
                                // Batch finished (either fully done or between batches)
                                // The frontend loop will handle re-triggering if needed
                                setIsGenerating(false);
                            }
                        }
                    }
                });
             } catch (e) {
                 console.error("Error setting up listener:", e);
             }
        };

        setupListener();
        return () => unsubscribe();
    }, []); // Removed dependency on isGenerating to prevent re-attaching constantly

    // Failed Items State
    const [failedItems, setFailedItems] = useState<any[]>([]);
    const [isLoadingFailed, setIsLoadingFailed] = useState(false);

    // Fetch Failed Items (Persistent)
    const fetchFailedItems = async () => {
        setIsLoadingFailed(true);
        try {
            const { db } = await import('../../config/firebase');
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            
            const q = query(collection(db, 'sops'), where('embeddingStatus', '==', 'failed'));
            const snapshot = await getDocs(q);
            
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setFailedItems(items);
        } catch (e) {
            console.error("Error fetching failed items:", e);
        } finally {
            setIsLoadingFailed(false);
        }
    };

    // Live Polling for Accurate Counts during Generation
    React.useEffect(() => {
        let interval: NodeJS.Timeout;

        const fetchLiveCounts = async () => {
            // Check implicit state via closure if needed, but the interval is controlled by the outer effect.
            // We proceed to fetch if the effect mounted.
            try {
                const { db } = await import('../../config/firebase');
                const { collection, query, where, getCountFromServer } = await import('firebase/firestore');
                
                const qCompleted = query(collection(db, 'sops'), where('embeddingStatus', '==', 'completed'));
                const snap = await getCountFromServer(qCompleted);
                const realCount = snap.data().count;

                setStats(prev => ({
                    ...prev,
                    lastEmbeddedCount: realCount,
                    // Estimate progress based on this live count for better UX
                    progress: prev.totalSOPs > 0 ? Math.round((realCount / prev.totalSOPs) * 100) : 0
                }));
            } catch (e) {
                console.error("Polling error:", e);
            }
        };

        if (isGenerating || stats.status === 'processing' || stats.status === 'stale') {
            fetchLiveCounts(); // Immediate fetch
            interval = setInterval(fetchLiveCounts, 5000); // Poll every 5s
        }

        return () => clearInterval(interval);
    }, [isGenerating, stats.status, stats.totalSOPs]);

    // Load failed items on mount and when gen status changes (to refresh list)
    React.useEffect(() => {
        fetchFailedItems();
    }, [status]); 

    // --- ACTIONS ---

    const handleStop = async () => {
        stopRef.current = true;
        setGenResult('Stopping... (Force unlocking backend)');
        setStatus('stopping');
        
        // Force unlock remote status if it's stuck
        try {
            const { db } = await import('../../config/firebase');
            const { doc, updateDoc } = await import('firebase/firestore');
            await updateDoc(doc(db, 'sophieStats', 'embeddings'), {
                status: 'idle'
            });
        } catch (e) {
            console.warn('Failed to force-update status:', e);
        }
        
        // Immediately reset UI state so user doesn't have to refresh
        // The background request will eventually return/fail, but we don't wait for it
        setTimeout(() => {
            setIsGenerating(false);
            setStatus('idle');
            setGenResult('Process stopped by user. Some items may have been processed in the last batch.');
        }, 1500); // Short delay for visual feedback
    };

    /**
     * Generic Processor for Batches
     * Handles: Loop, Adaptive Sizing, Stopping, Errors, Iteration Limit
     */
    const processBatchLoop = async (
        startBatchSize: number, 
        initialForce: boolean, 
        targetFnName: string,
        targetPayload: any = {},
        maxIterations?: number // NEW: Optional iteration limit
    ) => {
        setIsGenerating(true);
        stopRef.current = false; // Reset stop
        setStatus('idle');
        const limitMsg = maxIterations ? ` | Max ${maxIterations} iteration(s)` : ' | Until complete';
        setGenResult(`Initializing... (Batch: ${startBatchSize}${limitMsg})`);
        setFailedItems([]);

        try {
            const { functionsUsEast1 } = await import('../../config/firebase');
            const { httpsCallable } = await import('firebase/functions');
            // 9 Minute Timeout
            const cloudFn = httpsCallable(functionsUsEast1, targetFnName, { timeout: 540000 });
            
            let isContinuation = false;
            let keepGoing = true;
            let currentBatchSize = startBatchSize;
            let iterationCount = 0; // NEW: Track iterations
            
            while (keepGoing) {
                // 1. CHECK STOP
                if (stopRef.current) {
                    setGenResult('Process stopped by user.');
                    setStatus('idle');
                    setIsGenerating(false);
                    return; // EXIT
                }

                // 1.5 CHECK ITERATION LIMIT (NEW)
                if (maxIterations && iterationCount >= maxIterations) {
                    setGenResult(`Completed ${iterationCount} iteration(s) as requested. More items may remain.`);
                    setStatus('success');
                    setIsGenerating(false);
                    return;
                }

                try {
                     // 2. EXECUTE BATCH
                    const payload = { 
                        ...targetPayload,
                        force: initialForce && !isContinuation ? true : false, // Only force first run if requested
                        batchSize: currentBatchSize,
                        isContinuation 
                    };
                    
                    // @ts-ignore
                    const result = await cloudFn(payload);
                    const data = result.data as any;
                    const remaining = data.results?.remaining || 0;
                    iterationCount++; // NEW: Increment counter
                    
                    // 3. CHECK REMAINING
                    if (remaining > 0) {
                        isContinuation = true;
                        const limitInfo = maxIterations ? ` (${iterationCount}/${maxIterations} iterations)` : '';
                        setGenResult(`Batch ${iterationCount} done. ${remaining} items remaining...${limitInfo}`);
                    } else {
                        keepGoing = false;
                        setGenResult(`All items processed successfully in ${iterationCount} iteration(s).`);
                    }
                } catch (loopError: any) {
                    // 4. ADAPTIVE ERROR HANDLING
                    if (stopRef.current) return; // Ignore errors if we stopped

                    const errorMsg = loopError.message?.toLowerCase() || '';
                    
                    // A. TIMEOUT -> Throttle
                    if (loopError.code === 'deadline-exceeded' || errorMsg.includes('timeout')) {
                        console.warn('Timeout detected. Throttling...');
                        const newSize = Math.max(1, Math.floor(currentBatchSize * 0.75));
                        setGenResult(`Timeout. Retrying with size ${newSize}...`);
                        currentBatchSize = newSize;
                        await new Promise(r => setTimeout(r, 3000));
                        continue; 
                    } 
                    
                    // B. NETWORK / INTERNAL ERROR -> Retry (don't shrink batch, just wait)
                    // "Failed to fetch" often means net::ERR_DELETED or connection reset on local emulator
                    if (errorMsg.includes('network') || errorMsg.includes('failed to fetch') || errorMsg.includes('internal')) {
                         console.warn('Network/Internal error detected. Retrying...');
                         setGenResult(`Network glitch (${errorMsg}). Retrying in 5s...`);
                         await new Promise(r => setTimeout(r, 5000));
                         continue;
                    }

                    throw loopError; // Fatal error breaks loop
                }
            }
            setStatus('success');
            
        } catch (e: any) {
            console.error(e);
            setGenResult(`Error: ${e.message}`);
            setStatus('error');
        } finally {
            if (!stopRef.current) setIsGenerating(false);
        }
    }; 

    const handleGenerateEmbeddings = () => {
        const maxIterations = iterationLimit.enabled ? iterationLimit.count : undefined;
        processBatchLoop(batchSize[0], false, 'generateSOPEmbeddings', {}, maxIterations);
    };

    const handleRetryFailed = async () => {
        if (!window.confirm(`Retry ${failedItems.length} failed items?`)) return;
        
        // First, reset their status
        try {
             const { functionsUsEast1 } = await import('../../config/firebase');
             const { httpsCallable } = await import('firebase/functions');
             const resetFn = httpsCallable(functionsUsEast1, 'resetSOPEmbeddings', { timeout: 60000 });
             setGenResult('Resetting status...');
             // @ts-ignore
             await resetFn({ statusToReset: 'failed' });
        } catch(e) {
            setGenResult('Failed to reset status. Aborting.');
            return;
        }

        // Then process normally (isContinuation=false will pick them up as pending)
        processBatchLoop(batchSize[0], false, 'generateSOPEmbeddings');
    };

    const handleResetEmbeddings = async () => {
        if (!window.confirm("ARE YOU SURE? This will reset the 'processed' status of ALL SOPs, forcing a full re-generation next time. Existing vectors in the vector DB are NOT deleted, but they will be overwritten if you run generation again.")) {
            return;
        }
        
        setStatus('idle');
        setGenResult('Resetting database...');
        setIsGenerating(true); 

        try {
            const { functionsUsEast1 } = await import('../../config/firebase');
            const { httpsCallable } = await import('firebase/functions');
            const resetFn = httpsCallable(functionsUsEast1, 'resetSOPEmbeddings');
            
            // @ts-ignore
            const result = await resetFn({ statusToReset: 'all' });
            
            // @ts-ignore
            setGenResult(`Reset Complete. ${result.data.count} documents reset.`);
            setStatus('success');
            
            // Refresh stats
             setStats(prev => ({
                ...prev,
                lastEmbeddedCount: 0,
                progress: 0,
                status: 'idle'
            }));
            
        } catch (e: any) {
            console.error(e);
            setGenResult(e.message);
            setStatus('error');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Total SOPs</CardDescription>
                        <CardTitle className="text-2xl">{stats.totalSOPs}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Last Sync Date</CardDescription>
                        <CardTitle className="text-lg">{stats.lastSyncDate}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Embedded Documents</CardDescription>
                        <CardTitle className="text-2xl">{stats.lastEmbeddedCount} <span className="text-sm text-muted-foreground font-normal">/ {stats.totalSOPs}</span></CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Last Run</CardDescription>
                        <CardTitle className="text-lg">{stats.lastEmbeddedDate}</CardTitle>
                    </CardHeader>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Bot className="h-6 w-6 text-primary" />
                        <CardTitle>Sophie AI Configuration</CardTitle>
                    </div>
                    <CardDescription>
                        Manage the AI assistant's knowledge base and settings.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-4">
                        <div className="rounded-lg border p-4 bg-muted/50">
                            <h3 className="font-medium mb-4 flex items-center gap-2">
                                <RefreshCw className="h-4 w-4" />
                                Vector Embeddings Generation
                            </h3>

                            <Alert className="mb-6 border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200">
                                <AlertCircle className="h-4 w-4 stroke-yellow-600 dark:stroke-yellow-400" />
                                <AlertTitle className="text-yellow-800 dark:text-yellow-200">Cost Warning</AlertTitle>
                                <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                                    This action consumes Gemini API credits for every SOP processed. 
                                    Please be mindful and only run this when necessary (e.g., after significant content updates).
                                </AlertDescription>
                            </Alert>

                            {/* Batch Size Slider */}
                            <div className="mb-6 space-y-3">
                                <div className="flex justify-between items-center">
                                    <label className="text-sm font-medium">Batch Size: {batchSize[0]}</label>
                                    <span className="text-xs text-muted-foreground">Range: 1 - 50</span>
                                </div>
                                <Slider
                                    value={batchSize}
                                    onValueChange={setBatchSize}
                                    max={50}
                                    min={1}
                                    step={1}
                                    disabled={isGenerating}
                                    className="w-full"
                                />
                                <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded text-xs text-blue-800 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                                    <strong>Guidelines for Batch Size:</strong>
                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                        <li><strong>Small (1-10):</strong> Debugging & testing.</li>
                                        <li><strong>Medium (10-30):</strong> Safe default for daily updates.</li>
                                        <li><strong>Max (30-50):</strong> Fastest. Auto-chaining enabled (will split large jobs automatically to prevent timeouts).</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Iteration Limit Control - NEW */}
                            <div className="mb-6 p-4 rounded-xl border-2 border-dashed border-indigo-200 dark:border-indigo-800 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/30 dark:to-purple-950/30">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
                                            <Zap className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                Limit Iterations
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                Run a specific number of batches instead of all remaining
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={iterationLimit.enabled}
                                        onCheckedChange={(checked) => setIterationLimit(prev => ({ ...prev, enabled: checked }))}
                                        disabled={isGenerating}
                                        className="data-[state=checked]:bg-indigo-600"
                                    />
                                </div>
                                
                                {iterationLimit.enabled && (
                                    <div className="mt-4 flex items-center gap-3 animate-in slide-in-from-top-2 fade-in duration-200">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">Run</span>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={100}
                                            value={iterationLimit.count}
                                            onChange={(e) => setIterationLimit(prev => ({ ...prev, count: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) }))}
                                            className="w-20 h-8 text-center font-mono font-bold text-lg border-indigo-300 dark:border-indigo-700 focus:ring-indigo-500"
                                            disabled={isGenerating}
                                        />
                                        <span className="text-sm text-muted-foreground">iteration(s) then stop</span>
                                        <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium">
                                            ≈ {iterationLimit.count * batchSize[0]} docs
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {/* PROGRESS BAR */}
                            {(isGenerating || stats.status === 'processing') && (
                                <div className="mb-4 space-y-2">
                                     <div className="flex justify-between text-sm font-medium">
                                        <span>{status === 'stopping' ? 'Stopping...' : 'Generating...'}</span>
                                        {/* @ts-ignore - stats is extended in setStats */}
                                        <span>{stats.currentJobProcessed || 0} / {stats.totalCount} ({stats.progress}%)</span>
                                    </div>
                                    <Progress value={stats.progress} className={`h-2 ${status === 'stopping' ? 'opacity-50' : ''}`} />
                                </div>
                            )}

                            <div className="flex gap-2">
                                {!isGenerating ? (
                                    <Button 
                                        onClick={handleGenerateEmbeddings} 
                                        className="w-full sm:w-auto"
                                    >
                                        <CloudLightning className="mr-2 h-4 w-4" />
                                        Start Generation
                                    </Button>
                                ) : (
                                    <Button 
                                        onClick={handleStop} 
                                        variant="destructive"
                                        className="w-full sm:w-auto animate-pulse"
                                        disabled={status === 'stopping'}
                                    >
                                        <AlertCircle className="mr-2 h-4 w-4" />
                                        {status === 'stopping' ? 'Stopping...' : 'Stop Generation'}
                                    </Button>
                                )}
                            </div>
                        </div>
                        
                        {/* Failed Items Table (Persistent) */}
                        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 overflow-hidden">
                            <div className="p-4 border-b border-red-200 dark:border-red-800/50 flex justify-between items-center bg-red-100/50 dark:bg-red-900/20">
                                <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" />
                                    Failed Documents ({failedItems.length})
                                </h4>
                                <div className="flex items-center gap-2">
                                    {failedItems.length > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 dark:hover:bg-red-900/40"
                                            onClick={handleRetryFailed}
                                            disabled={isGenerating}
                                        >
                                            <RefreshCw className={`h-3 w-3 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                                            Retry Failed
                                        </Button>
                                    )}
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 text-xs text-red-700 hover:text-red-800 hover:bg-red-200 dark:hover:bg-red-900/40"
                                        onClick={fetchFailedItems}
                                    >
                                        <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingFailed ? 'animate-spin' : ''}`} />
                                        Refresh List
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="max-h-[400px] overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-red-200 hover:bg-transparent">
                                            <TableHead className="w-[30%] text-red-700 dark:text-red-300 font-medium">Document</TableHead>
                                            <TableHead className="w-[50%] text-red-700 dark:text-red-300 font-medium">Error Reason</TableHead>
                                            <TableHead className="w-[20%] text-right text-red-700 dark:text-red-300 font-medium">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {failedItems.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                                                    No failed documents found. Great job!
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            failedItems.map((item, idx) => (
                                                <TableRow key={idx} className="border-red-100 dark:border-red-800/30 hover:bg-red-100/30 dark:hover:bg-red-900/10">
                                                    <TableCell className="font-medium text-xs py-3 align-top">
                                                        <span className="line-clamp-2">{item.title || item.id}</span>
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground py-3 align-top">
                                                        <span className="line-clamp-2">{item.embeddingError || item.error || 'Unknown error'}</span>
                                                    </TableCell>
                                                    <TableCell className="text-right py-3 align-top">
                                                        {item.link ? (
                                                            <a 
                                                                href={item.link} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="inline-flex items-center text-xs text-blue-600 hover:underline hover:text-blue-800"
                                                            >
                                                                Open <ExternalLink className="h-3 w-3 ml-1" />
                                                            </a>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">No Link</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        {status === 'error' && (
                             <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Operation Failed</AlertTitle>
                                <AlertDescription>
                                    {genResult}
                                </AlertDescription>
                             </Alert>
                        )}
                         
                        {status === 'success' && !isGenerating && (
                            <Alert variant="default" className="border-green-500 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200">
                                <CheckCircle2 className="h-4 w-4 stroke-green-600 dark:stroke-green-400" />
                                <AlertTitle className="text-green-800 dark:text-green-200">Success</AlertTitle>
                                <AlertDescription className="text-green-700 dark:text-green-300">
                                    {genResult || 'Operation completed successfully.'}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="pt-4 border-t mt-4">
                            <h3 className="text-sm font-medium mb-3 text-muted-foreground">Danger Zone</h3>
                            <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={handleResetEmbeddings}
                                disabled={isGenerating}
                                className="w-full sm:w-auto"
                            >
                                Reset All Embeddings Status
                            </Button>
                            <p className="text-[10px] text-muted-foreground mt-2">
                                * This will force the system to re-process all embedded documents from scratch next time you run "Generate".
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SophieManagementSection;
